import {
  type IWebAnalyticsEntityHistory,
  type IWebAnalyticsLastInspection,
} from '@mahawi/eshop-common';
import { Descriptions, Space, Statistic, Tag, Typography } from 'antd';
import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { COLOR, FONT_SIZE, SPACING } from '../../theme/tokens';
import { formatMetric } from './format';

const { Text } = Typography;

type TEntityTotal = keyof IWebAnalyticsEntityHistory['totals'];

export const ENTITY_TOTALS: { metric: TEntityTotal; title: string }[] = [
  { metric: 'clicks', title: 'Clicks' },
  { metric: 'impressions', title: 'Impressions' },
  { metric: 'ctr', title: 'CTR' },
  { metric: 'position', title: 'Position' },
  { metric: 'units', title: 'Units sold' },
  { metric: 'revenueEur', title: 'Revenue' },
  { metric: 'estimatedMarginEur', title: 'Margin' },
  { metric: 'stock', title: 'Stock' },
];

export const describeInspection = (
  inspection: IWebAnalyticsLastInspection,
): { key: string; label: string; children: React.ReactNode }[] => [
  { key: 'inspectedOn', label: 'Inspected', children: inspection.inspectedOn },
  {
    key: 'isIndexed',
    label: 'Indexed',
    children: (
      <Tag color={inspection.isIndexed ? 'green' : 'red'}>
        {inspection.isIndexed ? 'Yes' : 'No'}
      </Tag>
    ),
  },
  {
    key: 'coverageState',
    label: 'Coverage',
    children: inspection.coverageState ?? EMPTY_VALUE,
  },
  {
    key: 'googleCanonical',
    label: 'Google canonical',
    children: inspection.googleCanonical ?? EMPTY_VALUE,
  },
  {
    key: 'userCanonical',
    label: 'Declared canonical',
    children: inspection.userCanonical ?? EMPTY_VALUE,
  },
  {
    key: 'richResultTypes',
    label: 'Rich results',
    children:
      inspection.richResultTypes.length > 0
        ? inspection.richResultTypes.map((type: string): React.ReactElement => (
            <Tag key={type}>{type}</Tag>
          ))
        : EMPTY_VALUE,
  },
];

export function EntityTotal({
  metric,
  title,
  value,
}: {
  metric: TEntityTotal;
  title: string;
  value: number | null;
}): React.ReactElement {
  return (
    <Statistic
      title={
        metric === 'estimatedMarginEur' ? (
          <Space size={SPACING.xs}>
            {title}
            <Tag color="gold">estimate</Tag>
          </Space>
        ) : (
          title
        )
      }
      value={formatMetric(metric, value)}
      styles={{ content: { fontSize: FONT_SIZE.lg } }}
    />
  );
}

function EntityHistory({
  history,
}: {
  history: IWebAnalyticsEntityHistory;
}): React.ReactElement {
  return (
    <Space orientation="vertical" size={SPACING.md} style={{ width: '100%' }}>
      <Text type="secondary" style={{ fontSize: FONT_SIZE.sm }}>
        Search and sales history {history.dateFrom} – {history.dateTo}
      </Text>

      <Space size={SPACING.xl} wrap>
        {ENTITY_TOTALS.map(({ metric, title }): React.ReactElement => (
          <EntityTotal
            key={metric}
            metric={metric}
            title={title}
            value={history.totals[metric]}
          />
        ))}
      </Space>

      <ResponsiveContainer width="100%" height={160}>
        <LineChart
          data={history.series}
          margin={{ top: SPACING.sm, right: SPACING.sm, bottom: 0, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={COLOR.borderDefault} />
          <XAxis dataKey="date" tick={{ fontSize: FONT_SIZE.xs }} />
          <YAxis
            yAxisId="search"
            tick={{ fontSize: FONT_SIZE.xs }}
            width={40}
          />
          <YAxis
            yAxisId="sales"
            orientation="right"
            tick={{ fontSize: FONT_SIZE.xs }}
            width={50}
          />
          <RechartsTooltip />
          <Line
            yAxisId="search"
            type="monotone"
            dataKey="clicks"
            name="Clicks"
            stroke={COLOR.primary}
            dot={false}
            connectNulls={false}
          />
          <Line
            yAxisId="sales"
            type="monotone"
            dataKey="revenueEur"
            name="Revenue (EUR)"
            stroke={COLOR.green[6]}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>

      {history.lastInspection ? (
        <Descriptions
          title="Last URL inspection"
          size="small"
          column={2}
          items={describeInspection(history.lastInspection)}
        />
      ) : (
        <Text type="secondary">No URL inspection yet</Text>
      )}
    </Space>
  );
}

export default EntityHistory;
